/**
 * Design-step-specific colour / gradient literals.
 *
 * Ported from the standalone Configurator's `features/design/*` (Tailwind classes
 * + inline gradients). These are the literals that ONLY the Design step uses and
 * therefore don't belong in the shared `configurator-tokens.ts`. Anything already
 * covered by the base palette re-uses `CFG` rather than re-declaring a hex.
 *
 * Rule for this area: no raw colour/gradient literal lives in a component file —
 * markup pulls every colour from a token. This file is the single home for the
 * Design step's own literals.
 */

import { themed } from '../../../../theme/themeRegistry';
import { CFG } from '../../../../theme/configurator-tokens';

export const DESIGN = themed('cfgDesign', {
  // Hairlines (Tailwind `border-white/10` → 0.10, `border-white/25` → 0.25). The
  // 0.10 variant matches CFG.hairline; the 0.25 hover variant is unique here.
  cardBorder: CFG.hairline,
  cardBorderHover: 'rgba(255, 255, 255, 0.25)',

  // Tile / surface fills.
  logoTile: CFG.cardAlt, // #1c1c20 — square logo backdrop
  // Opaque dark base under the featured card's wash + overlay. Production renders
  // the card transparent and relies on a dark backdrop; giving it an explicit base
  // means a lighter backdrop (e.g. the live app's sidebar) can't bleed through the
  // overlay's clear edge as a "white wash". Matches production's --card-bg.
  cardBase: '#121214',
  styleTile: '#0d0d0f', // StyleCard thumbnail backdrop
  catalogResultIconBorder: '#1a1c2e', // 0.78px ring on the catalog result logo

  // Text greys used by the cards (Tailwind `text-[#a1a1aa]`).
  subtitle: CFG.textMuted,

  // Catalog "Use this theme" success button.
  useThemeBorder: '#095028',
  useThemeBg: 'rgba(23, 201, 100, 0.12)',
  useThemeBgHover: 'rgba(23, 201, 100, 0.2)',
  useThemeText: CFG.success, // #17c964

  // Inline-SVG stroke colours (catalog chrome).
  iconStroke: CFG.base50, // #fafafa — back arrow
  checkStroke: CFG.success, // #17c964 — confirm check

  // "New" theme badge: a brand-orange wash + hairline; its label uses CFG.brandText.
  newBadgeBg: 'rgba(255, 121, 27, 0.15)',
  newBadgeBorder: 'rgba(255, 121, 27, 0.45)',
} as const);

export const DESIGN_GRADIENTS = themed('cfgDesignGradients', {
  /** Featured / current-theme card warm radial wash (bottom-right brand glow). */
  featuredWash:
    'radial-gradient(120% 120% at 100% 100%, rgba(255,121,27,0.48) 0%, rgba(191,91,20,0.34) 32%, rgba(128,61,14,0.2) 55%, rgba(0,0,0,0) 78%)',
  /** Featured card legibility overlay — exact production (single dark left-to-clear). */
  featuredOverlay: 'linear-gradient(110deg, rgba(17, 16, 21, 0.92) 26%, rgba(26, 26, 26, 0) 93%)',
  /** Compact theme card legibility overlay. */
  compactOverlay: 'linear-gradient(125deg, rgba(17,16,21,0.92) 26%, rgba(26,26,26,0) 93%)',
  /** Catalog result row legibility overlay (slightly denser). */
  resultOverlay: 'linear-gradient(124deg, rgba(17,16,21,0.97) 65%, rgba(26,26,26,0) 93%)',
  /** Catalog result row logo tile fill. */
  resultIconFill: 'linear-gradient(135deg, rgb(34,40,44) 16%, rgb(35,37,38) 84%)',
  /** Small "New" badge over a logo tile: the badge's orange wash (DESIGN.newBadgeBg) layered on
   *  the opaque card base (DESIGN.cardBase), so the logo art can't show through the label. */
  newBadgeSmallFill: `linear-gradient(${DESIGN.newBadgeBg}, ${DESIGN.newBadgeBg}), ${DESIGN.cardBase}`,
} as const);
